import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { IaqInsightData, ReportSensorThresholds } from '../../../../models/common';
import { Store } from '../../../../reducers';
import DeviceName from './DeviceName';
import styles from './SensorDataTable.module.scss';
import TableRowSensorStatus from './TableRowSensorStatus';

export type SensorDataTableProps = {
    sensorData: IaqInsightData;
    sensor: SensorTypes;
    sensorThreshold: ReportSensorThresholds;
};

type DeviceRow = {
    serialNumber: string;
    name: string;
    floor?: number;
    roomType?: string;
    average?: number;
    lowestMeasurement?: number;
    highestMeasurement?: number;
};

export const SensorDataTable: React.FC<SensorDataTableProps> = ({ sensorData, sensor, sensorThreshold }) => {
    const { t: txt } = useTranslation();
    const devices = useSelector((state: Store) => state.devices.devicesWithKeyInfo);
    const segmentProperties = useSelector((state: Store) => state.segmentPropertiesStore.segmentProperties);

    // Transform device data into rows
    const rows: DeviceRow[] = Object.keys(sensorData.devicesOpeningHoursValues || {})
        .map(serialNumber => {
            const device = devices[serialNumber];
            const segmentProps = segmentProperties[serialNumber];
            const deviceData = sensorData.devicesOpeningHoursValues[serialNumber];
            return {
                serialNumber,
                name: device?.segmentName || '',
                floor: segmentProps?.floor,
                roomType: segmentProps?.roomType,
                average: deviceData?.average,
                lowestMeasurement: deviceData?.lowestMeasurement,
                highestMeasurement: deviceData?.highestMeasurement,
            };
        })
        .sort((a, b) => {
            // Sort by floor first, then by name
            if (a.floor !== undefined && b.floor !== undefined) {
                if (a.floor !== b.floor) {
                    return a.floor - b.floor;
                }
            }
            return a.name.localeCompare(b.name);
        });

    return (
        <div className={styles.tableContainer}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.floorColumn}>{txt('Floor')}</th>
                        <th className={styles.spaceColumn}>{txt('SpaceName')}</th>
                        <th className={styles.valueColumn}>{txt('AverageValue')}</th>
                        <th className={styles.valueColumn}>{txt('InsightIaq.LowestMeasurement')}</th>
                        <th className={styles.valueColumn}>{txt('InsightIaq.HighestMeasurement')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <React.Fragment key={row.serialNumber}>
                            <tr className={styles.row}>
                                <td className={styles.floorColumn}>{row.floor !== undefined ? row.floor : '-'}</td>
                                <td className={styles.spaceColumn}>
                                    <DeviceName name={row.name} />
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={sensorThreshold}
                                        sensor={sensor}
                                        isAverage
                                        value={row.average}
                                    />
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={sensorThreshold}
                                        sensor={sensor}
                                        isAverage={false}
                                        value={row.lowestMeasurement}
                                    />
                                </td>
                                <td className={styles.valueColumn}>
                                    <TableRowSensorStatus
                                        sensorRanges={sensorThreshold}
                                        sensor={sensor}
                                        isAverage={false}
                                        value={row.highestMeasurement}
                                    />
                                </td>
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
